import { formatYmdLocal } from "@/lib/utils/date/formatYmdLocal";
import {
  AttendanceStatus,
  type GetAttendanceLogResponse,
} from "@/openapi/generated/models";
import type { MemberClassScheduleItem } from "@/features/dashboard/member/types/memberClassSchedule";

export const MEMBER_SCHEDULE_ATTENDANCE_LOOKBACK_DAYS = 365;

export function memberScheduleAttendanceDateRange(): {
  fromDate: Date;
  toDate: Date;
} {
  const toDate = new Date();
  toDate.setHours(12, 0, 0, 0);
  const fromDate = new Date(toDate);
  fromDate.setDate(
    fromDate.getDate() - MEMBER_SCHEDULE_ATTENDANCE_LOOKBACK_DAYS,
  );
  return { fromDate, toDate };
}

export function attendanceLogDateYmd(log: GetAttendanceLogResponse): string {
  const d = log.date instanceof Date ? log.date : new Date(log.date);
  return formatYmdLocal(d);
}

export function scheduleAttendanceLookupKey(
  dateYmd: string,
  studentId: number,
  classId?: number | null,
  appointmentId?: number | null,
): string {
  if (appointmentId != null && Number.isFinite(Number(appointmentId))) {
    return `${dateYmd}:${studentId}:a:${Number(appointmentId)}`;
  }
  return `${dateYmd}:${studentId}:c:${Number(classId ?? 0)}`;
}

export function attendanceLogLookupKey(log: GetAttendanceLogResponse): string {
  const dateYmd = attendanceLogDateYmd(log);
  const studentId = Number(log.studentId);
  const appointmentId =
    log.appointmentId != null ? Number(log.appointmentId) : null;
  const classId = log.classId != null ? Number(log.classId) : null;
  return scheduleAttendanceLookupKey(
    dateYmd,
    studentId,
    classId,
    appointmentId,
  );
}

export function buildScheduleAttendanceLookup(
  logs: GetAttendanceLogResponse[],
): Map<string, AttendanceStatus> {
  const map = new Map<string, AttendanceStatus>();
  for (const log of logs) {
    map.set(attendanceLogLookupKey(log), log.status);
  }
  return map;
}

export function resolveScheduleItemAttendance(
  item: MemberClassScheduleItem,
  date: Date,
  lookup: Map<string, AttendanceStatus>,
): AttendanceStatus | null {
  const dateYmd = formatYmdLocal(date);
  const key = scheduleAttendanceLookupKey(
    dateYmd,
    item.studentId,
    item.classId,
    item.appointmentId,
  );
  return lookup.get(key) ?? null;
}

export function memberAttendanceStatusTone(
  status: AttendanceStatus,
): "success" | "danger" | "info" {
  if (status === AttendanceStatus.Absent) return "danger";
  if (status === AttendanceStatus.Late) return "info";
  return "success";
}
